"use client";

import { useCallback, useEffect, useState } from "react";
import type { EventRowsView } from "@/lib/odds-screen/live/types";

export const FIRST_PAINT_CAP_MS = 1000;

export type FirstPaintGate = { readonly open: boolean; readonly markPainted: () => void };

type GateState = { readonly key: string; readonly painted: boolean; readonly capped: boolean };

export function useFirstPaintGate<R>(live: EventRowsView<R>, topics: number, visible: number, key = ""): FirstPaintGate {
  const [state, setState] = useState<GateState>({ key, painted: false, capped: false });
  const answered = live.pending < topics;
  const rearm = state.key !== key || (live.rows.size === 0 && !answered && (state.painted || state.capped));
  if (rearm) setState({ key, painted: false, capped: false });
  const gate = rearm ? { key, painted: false, capped: false } : state;
  const settled = live.pending === 0;
  const waiting = !gate.painted && !gate.capped && answered && !settled;
  useEffect(() => {
    if (!waiting) return;
    const cap = () => setState((current) => (current.key === key && !current.painted ? { ...current, capped: true } : current));
    let idle: number | undefined;
    const timer = setTimeout(() => {
      if (typeof requestIdleCallback === "function") idle = requestIdleCallback(cap, { timeout: FIRST_PAINT_CAP_MS });
      else cap();
    }, FIRST_PAINT_CAP_MS);
    return () => {
      clearTimeout(timer);
      if (idle !== undefined) cancelIdleCallback(idle);
    };
  }, [waiting, key]);
  const markPainted = useCallback(() => setState((current) => (current.key === key && !current.painted ? { ...current, painted: true } : current)), [key]);
  return { open: settled || (visible > 0 && (gate.painted || gate.capped)), markPainted };
}
